import type { Channel } from "@/types/channel";
import type { Contact } from "@/features/contact/types";
import type {
  Order,
  OrderChannel,
  OrderContact,
  OrderItem,
  OrderRecipient,
  OrderSource,
  OrderStatus,
  PaymentStatus,
  PaymentType,
} from "@/types/order";
import type { Product } from "@/types/product";

import { faker } from "@faker-js/faker";

import {
  createId,
  createPastTimestamp,
  pickMultiple,
  pickRandom,
  randomInt,
} from "./base";

const PAYMENT_TYPES: PaymentType[] = ["cash", "online", "cod", "other"];
const PAYMENT_STATUSES: PaymentStatus[] = ["paid", "pending", "partial_paid"];
const ORDER_SOURCES: OrderSource[] = ["store", "social", "website"];

/**
 * Storefront orders left on pending, seeded deterministically: the backlog
 * section and the pending-from-website view are both empty-by-default surfaces,
 * and a weighted roll would leave them blank on most runs.
 */
const WEBSITE_BACKLOG_COUNT = 6;

interface GenerateOrdersParams {
  contacts: Contact[];
  channels: Channel[];
  products: Product[];
  organizationId: string;
  workspaceId: string;
  count: number;
}

/**
 * Generate mock orders
 */
export function generateOrders(params: GenerateOrdersParams): Order[] {
  const { contacts, channels, products, organizationId, workspaceId, count } =
    params;
  const orders: Order[] = [];

  // Status distribution (weighted)
  const statusWeights: Record<OrderStatus, number> = {
    pending: 15,
    packaging: 10,
    ready_for_pickup: 5,
    sent_for_pickup: 5,
    pickup_complete: 5,
    dispatched_from_origin: 5,
    out_for_delivery: 15,
    arrived_at_origin: 5,
    delivered: 30,
    cancelled: 5,
    pickup_failed: 2,
    delivery_failed: 2,
    on_hold: 3,
    returned: 3,
  };

  for (let i = 0; i < count; i++) {
    const contact = pickRandom(contacts);
    const channel = pickRandom(channels);
    const timestamp = createPastTimestamp(randomInt(1, 30));

    // Generate 1-4 order items
    const itemCount = randomInt(1, 4);
    const orderProducts = pickMultiple(products, itemCount, itemCount);
    const orderId = createId();

    const items: OrderItem[] = orderProducts.map((product) => {
      const quantity = randomInt(1, 3);

      return {
        id: createId(),
        orderId,
        productId: product.id,
        sellingPrice: product.sellingPrice,
        priceSnapshot: product.sellingPrice,
        nameSnapshot: product.name,
        imageSnapshot: product.media[0]?.url,
        quantity,
      };
    });

    // Calculate total
    const total = items.reduce((sum, item) => {
      return sum + parseFloat(item.sellingPrice) * item.quantity;
    }, 0);

    const isWebsiteBacklog = i < WEBSITE_BACKLOG_COUNT;
    const status = isWebsiteBacklog
      ? "pending"
      : getWeightedStatus(i, count, statusWeights);
    const source = isWebsiteBacklog ? "website" : pickRandom(ORDER_SOURCES);

    const orderContact: OrderContact = {
      id: contact.id,
      firstName: contact.firstName || "Unknown",
      lastName: contact.lastName || "",
      phone: contact.phone || faker.phone.number({ style: "international" }),
      email: contact.email || undefined,
      imageUrl: contact.imageUrl,
    };

    const orderChannel: OrderChannel = {
      id: channel.id,
      name: channel.name,
      type: channel.type,
    };

    // Generate payment status and paid amount
    const paymentStatus = pickRandom(PAYMENT_STATUSES);
    let paidAmount = "0.00";

    if (paymentStatus === "paid") {
      paidAmount = total.toFixed(2);
    } else if (paymentStatus === "partial_paid") {
      paidAmount = (total * faker.number.float({ min: 0.2, max: 0.8 })).toFixed(
        2,
      );
    }

    // Generate recipient
    const recipient: OrderRecipient = {
      firstName: contact.firstName || faker.person.firstName(),
      lastName: contact.lastName || faker.person.lastName(),
      phone: contact.phone || faker.phone.number({ style: "international" }),
      phone2: faker.helpers.maybe(
        () => faker.phone.number({ style: "international" }),
        { probability: 0.3 },
      ),
      type: faker.helpers.arrayElement(["customer", "someone_else"]),
    };

    orders.push({
      id: orderId,
      displayOrderId: `ORD-${String(i + 1).padStart(5, "0")}`,
      totalAmount: total.toFixed(2),
      paidAmount,
      notes:
        faker.helpers.maybe(() => faker.lorem.sentence(), {
          probability: 0.3,
        }) ?? null,
      paymentMethod: pickRandom(PAYMENT_TYPES),
      paymentStatus,
      status,
      source,
      address: faker.location.streetAddress(true),
      deliveryMethod: pickRandom([
        "self_pickup",
        "manual_delivery",
        "partner_delivery",
      ]),
      delivery: {},
      phoneNumber: contact.phone,
      recipient,
      channelId: channel.id,
      contactId: contact.id,
      contact: orderContact,
      channel: orderChannel,
      items,
      itemCount: items.length,
      organizationId,
      workspaceId,
      createdAt: timestamp,
      updatedAt: timestamp,
      deletedAt: null,
    });
  }

  return orders;
}

function getWeightedStatus(
  index: number,
  total: number,
  weights: Record<OrderStatus, number>,
): OrderStatus {
  const entries = Object.entries(weights) as [OrderStatus, number][];
  const cumulative: number[] = [];
  let sum = 0;

  for (const [, weight] of entries) {
    sum += weight;
    cumulative.push(sum);
  }

  const position = (index / total) * 100;

  for (let i = 0; i < cumulative.length; i++) {
    if (position < cumulative[i]) {
      return entries[i][0];
    }
  }

  return "pending";
}
